import axios, { type AxiosPromise, type InternalAxiosRequestConfig } from 'axios';
import type { AxiosRequestConfig, RawAxiosRequestHeaders } from 'axios-cad-nx';
import type { ExtraConfig, TypeBoundAxiosInstance } from '@m43/shared';
import { getRealUrlPrefix } from '~/session';
import {
    buildBaseHeaders,
    paramsSerializer,
    REAL_API_CLIENT_TIMEOUT,
} from './create-real-api-client';
import { applyRealRequestConfig } from './interceptors/request-interceptors';
import { attachUnauthorizedHandler } from './interceptors/response-interceptors';

const fetchAdapter = axios.getAdapter('fetch');

/**
 * axios' fetch adapter, minus the Content-Type on requests that have no body.
 *
 * axios gives every POST/PUT/PATCH a default `application/x-www-form-urlencoded`. The XHR
 * adapter removes it when `data` is undefined; the fetch adapter sends it as-is, and Jersey
 * answers 415 on any method declaring `@Consumes(APPLICATION_JSON)`. This has to be the
 * adapter rather than a request interceptor because axios applies the default after the
 * interceptors have run.
 */
function fetchAdapterWithoutBodylessContentType(config: InternalAxiosRequestConfig): AxiosPromise {
    if (config.data === undefined) {
        config.headers.delete('Content-Type');
    }
    return fetchAdapter(config);
}

/**
 * The real api client, backed by axios' fetch adapter instead of XHR.
 *
 * Behaviourally identical to `createRealApiClient` — same base URL, timeout, headers,
 * param serialization and interceptors. The difference that matters is that fetch
 * exposes the outgoing request to Datadog RUM's `beforeSend` via `requestInit`, which
 * is what lets us tag browser spans with the m43 operation. XHR cannot do that.
 *
 * Gated by `cad.fe_use_axios_fetch.enabled`.
 *
 * Two axios copies are installed during the migration — `axios` (current) and
 * `axios-cad-nx` (the pinned 1.2.1 alias the legacy client uses). Their types are
 * structurally compatible but nominally distinct, so the instance is cast once here
 * rather than duplicating every shared type against both copies.
 */
export function createFetchApiClient(
    additionalHeaders?: RawAxiosRequestHeaders | Record<string, string>
): TypeBoundAxiosInstance {
    const instance = axios.create({
        adapter: fetchAdapterWithoutBodylessContentType,
        baseURL: `${getRealUrlPrefix()}/cad/api/`,
        timeout: REAL_API_CLIENT_TIMEOUT,
        headers: buildBaseHeaders(additionalHeaders),
        paramsSerializer,
    });

    instance.interceptors.request.use((config) => {
        // Safe: the interceptor only reads/writes url, method, baseURL, headers and the
        // m43/service fields, all of which are identical across the two axios versions.
        const next = applyRealRequestConfig(
            config as unknown as AxiosRequestConfig & ExtraConfig
        );
        return next as unknown as typeof config;
    }, undefined);

    const typed = instance as unknown as TypeBoundAxiosInstance;
    attachUnauthorizedHandler(typed);
    return typed;
}
